import React, { createContext, useContext, useState } from 'react';
import { usersApi } from '../services/api';
import { useAuth } from './AuthContext';

const UsersContext = createContext();

export const useUsers = () => {
  const context = useContext(UsersContext);
  if (!context) {
    throw new Error('useUsers must be used within a UsersProvider');
  }
  return context;
};

export const UsersProvider = ({ children }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0,
    count: 0
  });

  const isAdmin = currentUser?.role === 'Admin';

  const fetchUsers = async (params = {}) => {
    if (!isAdmin) {
      setError('Access denied: Admin role required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await usersApi.listUsers({
        limit: pagination.limit,
        offset: pagination.offset,
        ...params
      });

      setUsers(response.items);
      setPagination({
        limit: response.limit,
        offset: response.offset,
        total: response.total,
        count: response.count
      });

      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (userData) => {
    if (!isAdmin) {
      throw new Error('Access denied: Admin role required');
    }

    try {
      setLoading(true);
      setError(null);

      const newUser = await usersApi.createUser(userData);
      setUsers(prev => [...prev, newUser]);
      
      return newUser;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, updateData) => {
    if (!isAdmin) {
      throw new Error('Access denied: Admin role required');
    }

    try {
      setLoading(true);
      setError(null);

      const updatedUser = await usersApi.updateUser(userId, updateData);
      setUsers(prev => prev.map(user => 
        user.id === userId ? updatedUser : user
      ));
      
      return updatedUser;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!isAdmin) {
      throw new Error('Access denied: Admin role required');
    }

    try {
      setLoading(true);
      setError(null);

      await usersApi.deleteUser(userId);
      setUsers(prev => prev.filter(user => user.id !== userId));
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getUser = async (userId) => {
    try {
      setError(null);
      return await usersApi.getUser(userId);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const value = {
    users,
    loading,
    error,
    pagination,
    isAdmin,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    getUser,
    setError
  };

  return (
    <UsersContext.Provider value={value}>
      {children}
    </UsersContext.Provider>
  );
};